import { z } from 'zod';
import {
  AiRuntimeError,
  aiModelRefSchema,
  aiRoleRuntimeConfigSchema,
  type AiModelRef,
  type AiRoleRuntimeConfig,
} from './runtime.js';
import { aiGuidedContentSchema, type AiGuidedContent } from './guided-content.js';
import {
  aiModelPolicySchema,
  deriveModelPolicyFromLegacyRuntime,
  type AiModelPolicy,
} from './model-policy.js';

export const AI_ROLE_PROMPT_MAX_CHARS = 12_000;
export const AI_ROLE_TEMPLATE_MAX_CHARS = 4_000;

export const aiRoleRuntimeEnvelopeSchema = z
  .object({
    primary: aiModelRefSchema,
    fallback: aiModelRefSchema.nullable().optional(),
    timeoutMs: z.number().int().positive().max(120_000),
    maxOutputTokens: z.number().int().positive().max(16_000),
    /** Legacy flat configs only; new documents use DB version as promptVersion. */
    promptVersion: z.number().int().positive().optional(),
  })
  .strict();

export type AiRoleRuntimeEnvelope = z.infer<typeof aiRoleRuntimeEnvelopeSchema>;

export const aiRolePromptSchema = z
  .object({
    system: z.string().max(AI_ROLE_PROMPT_MAX_CHARS),
  })
  .strict();

export type AiRolePromptConfig = z.infer<typeof aiRolePromptSchema>;

export const aiRoleTemplatesSchema = z
  .object({
    objections: z
      .object({
        price: z.string().max(AI_ROLE_TEMPLATE_MAX_CHARS),
        need_to_think: z.string().max(AI_ROLE_TEMPLATE_MAX_CHARS),
        no_time: z.string().max(AI_ROLE_TEMPLATE_MAX_CHARS),
      })
      .strict(),
    handoff: z
      .object({
        request_human: z.string().max(AI_ROLE_TEMPLATE_MAX_CHARS),
        safety: z.string().max(AI_ROLE_TEMPLATE_MAX_CHARS),
        provider_unavailable: z.string().max(AI_ROLE_TEMPLATE_MAX_CHARS),
      })
      .strict(),
  })
  .strict();

export type AiRoleTemplatesConfig = z.infer<typeof aiRoleTemplatesSchema>;

export const aiRoleConfigDocumentSchema = z
  .object({
    runtime: aiRoleRuntimeEnvelopeSchema,
    prompt: aiRolePromptSchema,
    templates: aiRoleTemplatesSchema,
    guidedContent: aiGuidedContentSchema.optional(),
    /** CDLIT: выбор Platform model id по классам задач (simple/main/complex/fallback). */
    modelPolicy: aiModelPolicySchema.optional(),
  })
  .strict();

export type AiRoleConfigDocument = z.infer<typeof aiRoleConfigDocumentSchema>;

export type AiRoleResolvedConfig = {
  runtime: AiRoleRuntimeConfig;
  prompt: AiRolePromptConfig;
  templates: AiRoleTemplatesConfig;
  guidedContent?: AiGuidedContent;
  modelPolicy?: AiModelPolicy;
};

export const aiRoleConfigDocumentInputSchema = aiRoleConfigDocumentSchema;

export function emptyAiRoleTemplates(): AiRoleTemplatesConfig {
  return {
    objections: { price: '', need_to_think: '', no_time: '' },
    handoff: { request_human: '', safety: '', provider_unavailable: '' },
  };
}

/**
 * Build a generic role document around a caller-selected model reference.
 * The shared contract deliberately has no preferred provider or model ID.
 */
export function createDefaultAiRoleConfigDocument(primary: AiModelRef): AiRoleConfigDocument {
  return {
    runtime: {
      primary: aiModelRefSchema.parse(primary),
      fallback: null,
      timeoutMs: 15_000,
      maxOutputTokens: 800,
    },
    prompt: { system: '' },
    templates: emptyAiRoleTemplates(),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

export function isLegacyRuntimeOnlyConfig(raw: unknown): boolean {
  if (!isRecord(raw)) return false;
  return 'primary' in raw && !('runtime' in raw);
}

function withDerivedModelPolicy(document: AiRoleConfigDocument): AiRoleConfigDocument {
  if (document.modelPolicy) {
    return document;
  }
  const derived = deriveModelPolicyFromLegacyRuntime(document.runtime);
  if (!derived) {
    return document;
  }
  return { ...document, modelPolicy: derived };
}

function stripKnownLegacyRoleFields(raw: unknown): unknown {
  if (!isRecord(raw) || !('cdlitProductSettings' in raw)) return raw;
  const { cdlitProductSettings: _migratedToDedicatedSsot, ...rest } = raw;
  return rest;
}

export function coerceAiRoleConfigDocument(raw: unknown): AiRoleConfigDocument {
  if (isLegacyRuntimeOnlyConfig(raw)) {
    const legacy = aiRoleRuntimeEnvelopeSchema.safeParse(raw);
    if (!legacy.success) {
      throw new AiRuntimeError('ai_config_invalid', 'Invalid legacy role runtime config');
    }
    const { promptVersion: _ignored, ...runtime } = legacy.data;
    return withDerivedModelPolicy({
      runtime,
      prompt: { system: '' },
      templates: emptyAiRoleTemplates(),
    });
  }

  const parsed = aiRoleConfigDocumentSchema.safeParse(stripKnownLegacyRoleFields(raw));
  if (!parsed.success) {
    throw new AiRuntimeError('ai_config_invalid', 'Invalid role config document');
  }
  return withDerivedModelPolicy(parsed.data);
}

export function parseAiRoleConfigDocument(raw: unknown): AiRoleConfigDocument {
  const parsed = aiRoleConfigDocumentSchema.safeParse(raw);
  if (!parsed.success) {
    throw new AiRuntimeError('ai_config_invalid', 'Invalid role config document');
  }
  return parsed.data;
}

function validateRuntimePair(runtime: AiRoleRuntimeEnvelope): void {
  const fallback = runtime.fallback ?? null;
  if (
    fallback &&
    fallback.provider === runtime.primary.provider &&
    fallback.model === runtime.primary.model &&
    fallback.connectionId === runtime.primary.connectionId
  ) {
    throw new AiRuntimeError('ai_config_invalid', 'Primary and fallback must differ');
  }
}

function resolvePromptVersion(raw: unknown, version?: number): number {
  if (isLegacyRuntimeOnlyConfig(raw)) {
    const legacy = aiRoleRuntimeEnvelopeSchema.safeParse(raw);
    if (legacy.success && legacy.data.promptVersion !== undefined && legacy.data.promptVersion > 0) {
      return legacy.data.promptVersion;
    }
  }

  if (version !== undefined && Number.isInteger(version) && version > 0) {
    return version;
  }

  throw new AiRuntimeError('ai_config_invalid', 'Missing promptVersion for role config');
}

function assembleRoleRuntimeConfig(
  roleKey: string,
  runtime: AiRoleRuntimeEnvelope,
  promptVersion: number,
  configVersion?: number,
): AiRoleRuntimeConfig {
  const fallback = runtime.fallback ?? null;
  return aiRoleRuntimeConfigSchema.parse({
    roleKey,
    primary: runtime.primary,
    fallback,
    timeoutMs: runtime.timeoutMs,
    maxOutputTokens: runtime.maxOutputTokens,
    promptVersion,
    ...(configVersion !== undefined ? { configVersion } : {}),
  });
}

export function buildAiRoleRuntimeConfig(
  roleKey: string,
  raw: unknown,
  version?: number,
): AiRoleRuntimeConfig {
  const document = coerceAiRoleConfigDocument(raw);
  validateRuntimePair(document.runtime);
  const promptVersion = resolvePromptVersion(raw, version);
  return assembleRoleRuntimeConfig(roleKey, document.runtime, promptVersion, version);
}

export function parseAiRoleRuntimeConfig(
  roleKey: string,
  raw: unknown,
  version?: number,
): AiRoleRuntimeConfig {
  return buildAiRoleRuntimeConfig(roleKey, raw, version);
}

export function resolveAiRoleConfig(
  roleKey: string,
  raw: unknown,
  version?: number,
): AiRoleResolvedConfig {
  const document = coerceAiRoleConfigDocument(raw);
  validateRuntimePair(document.runtime);
  const promptVersion = resolvePromptVersion(raw, version);

  const coerced = withDerivedModelPolicy(document);

  return {
    runtime: assembleRoleRuntimeConfig(roleKey, coerced.runtime, promptVersion, version),
    prompt: coerced.prompt,
    templates: coerced.templates,
    guidedContent: coerced.guidedContent,
    modelPolicy: coerced.modelPolicy,
  };
}

export const aiProviderStatusIdSchema = z.enum([
  'yandex',
  'deepseek',
  'openai',
  'anthropic',
  'openrouter',
  'openai_compatible',
  'remote_runner',
  'speechkit',
]);

export type AiProviderStatusId = z.infer<typeof aiProviderStatusIdSchema>;

export const aiProviderCapabilitySchema = z.enum(['text', 'stt']);
export type AiProviderCapability = z.infer<typeof aiProviderCapabilitySchema>;

export const aiProviderStatusSourceSchema = z.enum(['encrypted', 'env', 'none']);
export type AiProviderStatusSource = z.infer<typeof aiProviderStatusSourceSchema>;

export const aiProviderStatusSchema = z
  .object({
    id: aiProviderStatusIdSchema,
    configured: z.boolean(),
    source: aiProviderStatusSourceSchema,
    capabilities: z.array(aiProviderCapabilitySchema).min(1),
    updatedAt: z.string().datetime().nullable().optional(),
  })
  .strict();

export type AiProviderStatus = z.infer<typeof aiProviderStatusSchema>;

export const aiConfigVersionStatusSchema = z.enum(['draft', 'published', 'archived']);
export type AiConfigVersionStatus = z.infer<typeof aiConfigVersionStatusSchema>;

export const aiAdminRoleVersionViewSchema = z
  .object({
    versionId: z.string().uuid(),
    version: z.number().int().positive(),
    status: aiConfigVersionStatusSchema,
    createdAt: z.string().datetime(),
    publishedAt: z.string().datetime().nullable(),
  })
  .strict();

export type AiAdminRoleVersionView = z.infer<typeof aiAdminRoleVersionViewSchema>;
